import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { 
  FiArrowLeft, 
  FiUpload, 
  FiFile, 
  FiFileText, 
  FiImage, 
  FiDownload, 
  FiTrash2, 
  FiGrid,
  FiList,
  FiFolder,
  FiAlertCircle
} from 'react-icons/fi';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useAuth } from '../../../contexts/AuthContext';
import { foldersService, MAX_FILE_SIZE_BYTES, MAX_FILE_SIZE_LABEL } from '../../../services/folders.service';
import {
  Container,
  Header,
  BackButton,
  FolderInfo,
  FolderName,
  FolderStats,
  HeaderActions,
  ViewToggle,
  ViewButton,
  ActionButton,
  FilesContainer,
  FileCard,
  FileIcon,
  FileInfo,
  FileName,
  FileDetails,
  FileActions,
  ActionIcon,
  UploadArea,
  UploadText,
  FileInput,
  LoadingContainer,
  EmptyState,
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  CancelButton,
  ConfirmButton,
  ErrorMessage,
  SuccessMessage
} from './styles';

import {
  SearchContainer,
  SearchInput,
  PaginationContainer,
  PaginationInfo,
  PaginationButtons,
  PaginationButton
} from '../styles';

interface FileItem {
  id: string;
  name: string;
  originalName: string;
  type: string;
  size: number;
  uploadedAt: string;
  uploadedBy: string;
}

interface Folder {
  id: string;
  name: string;
  description?: string;
  fileCount: number;
  totalSize: number;
  permissions: { userId: string; level: 'read' | 'write' | 'admin' }[];
}

type ViewMode = 'grid' | 'list';

const ALLOWED_EXTENSIONS = ['.txt', '.docx', '.pdf', '.png', '.jpg', '.jpeg'];
const BLOCKED_EXTENSIONS = ['.exe', '.bat', '.cmd', '.msi', '.scr', '.com', '.pif', '.vbs', '.js', '.jar'];

const FolderDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user } = useAuth();
  
  const [folder, setFolder] = useState<Folder | null>(null);
  const [files, setFiles] = useState<FileItem[]>([]);
  const [filteredFiles, setFilteredFiles] = useState<FileItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [selectedFile, setSelectedFile] = useState<FileItem | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [message, setMessage] = useState<{ type: 'error' | 'success'; text: string } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20); // 20 arquivos por página

  useEffect(() => {
    if (id) {
      loadFolderData();
    }
  }, [id]);

  // Função para verificar permissões do usuário atual
  const getUserPermission = (): 'read' | 'write' | 'admin' | null => {
    if (!folder || !user) return null;
    
    const userPermission = folder.permissions.find(p => p.userId === user.id);
    return userPermission ? userPermission.level : null;
  };

  // Verificar se usuário pode fazer upload
  const canUploadFiles = (): boolean => {
    const permission = getUserPermission();
    return permission === 'write' || permission === 'admin';
  };

  // Verificar se usuário pode deletar arquivos
  const canDeleteFiles = (): boolean => {
    const permission = getUserPermission();
    return permission === 'admin';
  };

  // Verificar se usuário pode editar a pasta
  const canEditFolder = (): boolean => {
    const permission = getUserPermission();
    return permission === 'write' || permission === 'admin';
  };

  // Verificar se usuário pode gerenciar permissões da pasta
  const canManagePermissions = (): boolean => {
    const permission = getUserPermission();
    return permission === 'write' || permission === 'admin';
  };

  const loadFolderData = async () => {
    try {
      setLoading(true);
      console.log('Carregando dados da pasta com ID:', id);
      
      const [folderResponse, filesResponse] = await Promise.all([
        foldersService.getFolderById(id!),
        foldersService.getFolderFiles(id!)
      ]);
      
      console.log('Resposta da pasta:', folderResponse);
      console.log('Resposta dos arquivos:', filesResponse);
      
      setFolder(folderResponse.data);
      setFiles(filesResponse.data || []);
      setFilteredFiles(filesResponse.data || []);
    } catch (error: any) {
      console.error('Erro ao carregar dados da pasta:', error);
      showMessage('error', 'Erro ao carregar dados da pasta');
      
      // Para debug - mostrar o erro específico
      if (error?.response) {
        console.error('Status:', error.response.status);
        console.error('Data:', error.response.data);
      }
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type: 'error' | 'success', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  // Função de busca para arquivos
  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1); // Reset para primeira página ao buscar
    
    if (!term.trim()) {
      setFilteredFiles(files);
      return;
    }

    const filtered = files.filter(file =>
      file.originalName.toLowerCase().includes(term.toLowerCase()) ||
      file.name.toLowerCase().includes(term.toLowerCase()) ||
      file.type.toLowerCase().includes(term.toLowerCase())
    );
    setFilteredFiles(filtered);
  };

  // Aplicar filtro quando files mudarem
  React.useEffect(() => {
    handleSearch(searchTerm);
  }, [files]);

  // Calcular paginação
  const totalPages = Math.ceil(filteredFiles.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedFiles = filteredFiles.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const generatePageNumbers = () => {
    const pages: number[] = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      const halfVisible = Math.floor(maxVisiblePages / 2);
      let startPage = Math.max(1, currentPage - halfVisible);
      let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
      
      if (endPage - startPage < maxVisiblePages - 1) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }
      
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
    }
    
    return pages;
  };

  const validateFile = (file: File): string | null => {
    const fileName = file.name.toLowerCase();
    const extension = fileName.substring(fileName.lastIndexOf('.'));
    
    // Verificar extensões bloqueadas
    if (BLOCKED_EXTENSIONS.some(ext => fileName.endsWith(ext))) {
      return `Arquivo ${extension} não é permitido por questões de segurança.`;
    }
    
    // Verificar extensões permitidas
    if (!ALLOWED_EXTENSIONS.some(ext => fileName.endsWith(ext))) {
      return `Apenas arquivos ${ALLOWED_EXTENSIONS.join(', ')} são permitidos.`;
    }
    
    // Verificar tamanho — constante compartilhada com folders.service.ts (era 25MB duplicado aqui)
    if (file.size > MAX_FILE_SIZE_BYTES) {
      return `Arquivo muito grande. Tamanho máximo: ${MAX_FILE_SIZE_LABEL}.`;
    }
    
    return null;
  };

  const handleFileUpload = async (files: FileList) => {
    if (files.length === 0) return;
    
    setUploading(true);
    let successCount = 0;
    let errorMessages: string[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const error = validateFile(file);
      
      if (error) {
        errorMessages.push(`${file.name}: ${error}`);
        continue;
      }

      try {
        await foldersService.uploadFile(id!, file);
        successCount++;
      } catch (error) {
        console.error(`Erro ao fazer upload de ${file.name}:`, error);
        errorMessages.push(`${file.name}: Erro no upload`);
      }
    }

    if (successCount > 0) {
      showMessage('success', `${successCount} arquivo(s) enviado(s) com sucesso!`);
      await loadFolderData();
    }

    if (errorMessages.length > 0) {
      showMessage('error', errorMessages.join('\n'));
    }

    setUploading(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileUpload(e.dataTransfer.files);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFileUpload(e.target.files);
    }
  };

  const handleDeleteFile = async () => {
    if (!selectedFile) return;
    
    try {
      await foldersService.deleteFile(selectedFile.id);
      showMessage('success', 'Arquivo excluído com sucesso!');
      await loadFolderData();
    } catch (error) {
      console.error('Erro ao excluir arquivo:', error);
      showMessage('error', 'Erro ao excluir arquivo');
    }
    
    setShowDeleteModal(false);
    setSelectedFile(null);
  };

  const handleDownloadFile = async (file: FileItem) => {
    try {
      await foldersService.downloadFile(file.id, file.originalName);
      showMessage('success', 'Download iniciado!');
    } catch (error) {
      console.error('Erro ao fazer download:', error);
      showMessage('error', 'Erro ao fazer download do arquivo');
    }
  };

  const getFileIcon = (type: string) => {
    if (type.includes('image')) return FiImage({ size: 24 });
    if (type.includes('pdf')) return FiFileText({ size: 24 });
    if (type.includes('word') || type.includes('document')) return FiFileText({ size: 24 });
    return FiFile({ size: 24 });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <Container>
        <LoadingContainer>
          <div>Carregando pasta...</div>
        </LoadingContainer>
      </Container>
    );
  }

  if (!folder) {
    return (
      <Container>
                 <EmptyState>
           {FiAlertCircle({ size: 64 })}
           <h3>Pasta não encontrada</h3>
          <p>A pasta que você está procurando não existe ou foi removida.</p>
          <ActionButton onClick={() => navigate('/folders')}>
            Voltar para Pastas
          </ActionButton>
        </EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      {message && (
        <div style={{ 
          position: 'fixed', 
          top: '20px', 
          right: '20px', 
          zIndex: 1001,
          maxWidth: '400px'
        }}>
          {message.type === 'error' ? (
            <ErrorMessage>{message.text}</ErrorMessage>
          ) : (
            <SuccessMessage>{message.text}</SuccessMessage>
          )}
        </div>
      )}

      <Header>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                     <BackButton onClick={() => navigate('/folders')}>
             {FiArrowLeft({ size: 20 })}
           </BackButton>
          <FolderInfo>
            <FolderName>{folder.name}</FolderName>
            <FolderStats>
              {folder.fileCount} arquivo{folder.fileCount !== 1 ? 's' : ''} • {formatFileSize(folder.totalSize)}
            </FolderStats>
          </FolderInfo>
        </div>
        
                <HeaderActions>
          <ViewToggle>
            <ViewButton 
              active={viewMode === 'grid'} 
              onClick={() => setViewMode('grid')}
            >
              {FiGrid({ size: 18 })}
            </ViewButton>
            <ViewButton 
              active={viewMode === 'list'} 
              onClick={() => setViewMode('list')}
            >
              {FiList({ size: 18 })}
            </ViewButton>
          </ViewToggle>
          
          {canUploadFiles() && (
            <ActionButton onClick={() => document.getElementById('fileInput')?.click()}>
              {FiUpload({ size: 18 })}
              Upload
            </ActionButton>
          )}
        </HeaderActions>
      </Header>

            {canUploadFiles() && (
        <>
          <UploadArea 
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            dragActive={dragActive}
            onClick={() => document.getElementById('fileInput')?.click()}
          >
            {FiUpload({ size: 32 })}
            <UploadText>
              {uploading ? 
                'Enviando arquivos...' : 
                'Arraste arquivos aqui ou clique para selecionar'
              }
            </UploadText>
            <div style={{ fontSize: '12px', color: theme.textSecondary, marginTop: '4px' }}>
              Formatos permitidos: {ALLOWED_EXTENSIONS.join(', ')} • Máximo: {MAX_FILE_SIZE_LABEL}
            </div>
          </UploadArea>

          <FileInput
            id="fileInput"
            type="file"
            multiple
            accept={ALLOWED_EXTENSIONS.join(',')}
            onChange={handleInputChange}
          />
        </>
      )}

      <SearchContainer>
        <SearchInput
          type="text"
          placeholder="Buscar arquivos por nome ou tipo..."
          value={searchTerm}
          onChange={(e) => handleSearch(e.target.value)}
        />
      </SearchContainer>

      {filteredFiles.length === 0 ? (
                 <EmptyState>
           {FiFolder({ size: 64 })}
           <h3>{searchTerm && files.length > 0 ? 'Nenhum arquivo encontrado' : 'Pasta vazia'}</h3>
          <p>{searchTerm && files.length > 0 ? 'Tente ajustar os termos da busca' : 'Esta pasta ainda não possui arquivos. Faça upload dos primeiros arquivos.'}</p>
        </EmptyState>
      ) : (
        <>
          <FilesContainer viewMode={viewMode}>
            {paginatedFiles.map((file) => (
            <FileCard key={file.id} viewMode={viewMode}>
              <FileIcon>
                {getFileIcon(file.type)}
              </FileIcon>
              <FileInfo>
                <FileName title={file.originalName}>{file.originalName}</FileName>
                <FileDetails>
                  {formatFileSize(file.size)} • {new Date(file.uploadedAt).toLocaleDateString('pt-BR')}
                </FileDetails>
              </FileInfo>
                            <FileActions>
                <ActionIcon 
                  onClick={() => handleDownloadFile(file)}
                  title="Download"
                >
                  {FiDownload({ size: 16 })}
                </ActionIcon>
                {canDeleteFiles() && (
                  <ActionIcon 
                    onClick={() => {
                      setSelectedFile(file);
                      setShowDeleteModal(true);
                    }}
                    title="Excluir"
                    danger
                  >
                    {FiTrash2({ size: 16 })}
                  </ActionIcon>
                )}
              </FileActions>
            </FileCard>
          ))}
        </FilesContainer>

        {/* Paginação */}
        {totalPages > 1 && (
          <PaginationContainer>
            <PaginationInfo>
              Mostrando {startIndex + 1}-{Math.min(endIndex, filteredFiles.length)} de {filteredFiles.length} arquivos
            </PaginationInfo>
            <PaginationButtons>
              <PaginationButton
                disabled={currentPage === 1}
                onClick={() => handlePageChange(currentPage - 1)}
              >
                Anterior
              </PaginationButton>
              
              {generatePageNumbers().map((page) => (
                <PaginationButton
                  key={page}
                  active={currentPage === page}
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </PaginationButton>
              ))}
              
              <PaginationButton
                disabled={currentPage === totalPages}
                onClick={() => handlePageChange(currentPage + 1)}
              >
                Próxima
              </PaginationButton>
            </PaginationButtons>
          </PaginationContainer>
        )}
        </>
      )}

      {/* Modal Excluir Arquivo */}
      {showDeleteModal && selectedFile && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>Excluir Arquivo</ModalTitle>
              <ModalCloseButton onClick={() => setShowDeleteModal(false)}>×</ModalCloseButton>
            </ModalHeader>
            <ModalBody>
              <p>
                Tem certeza que deseja excluir o arquivo <strong>"{selectedFile.originalName}"</strong>?
              </p>
              <p style={{ color: theme.error, fontSize: '14px' }}>
                Esta ação não pode ser desfeita.
              </p>
            </ModalBody>
            <ModalFooter>
              <CancelButton onClick={() => setShowDeleteModal(false)}>Cancelar</CancelButton>
              <ConfirmButton onClick={handleDeleteFile} style={{ backgroundColor: theme.error }}>
                Excluir
              </ConfirmButton>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

export default FolderDetails; 